/**
 * Formats a number as a currency.
 * If the number is an integer, displays without decimals (e.g., $10).
 * If the number has decimals, displays with 2 decimal places (e.g., $10.50).
 *
 * @param {number} price - The price amount to format in dollars
 * @param {Intl.LocalesArgument} locale - optional locale value, defaults to en-US
 * @param {Intl.NumberFormatOptions["currency"]} currency - optional currency value, defaults to USD
 * @returns Formatted currency string (e.g., "$10" or "$10.50")
 *
 * @example
 * ```typescript
 * formatCurrency(1000) // "$1,000" (in USD)
 * formatCurrency(0) // "$0"
 * formatCurrency(10.5, "en-AU", "AUD") // "$10.50" (in AUD)
 * ```
 */

export function formatCurrency(
  price: number,
  locale: Intl.LocalesArgument = "en-US",
  currency: Intl.NumberFormatOptions["currency"] = "USD"
): string {
  const formatOptions: Intl.NumberFormatOptions = {
    style: "currency",
    currency,
  };

  if (Number.isInteger(price)) {
    return new Intl.NumberFormat(locale as string | string[], {
      ...formatOptions,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  } else {
    return new Intl.NumberFormat(locale as string | string[], {
      ...formatOptions,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  }
}
